import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Subscription, switchMap } from 'rxjs';

import { ReportQuery, ReportQueryService, ReportResult } from '../../../services/report-query';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';
import {
  dashboardErrorMessage,
  dashboardLayoutStyle,
} from '../../widget-and-graphs.models';

export interface DashboardListColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  format?: 'text' | 'number' | 'currency' | 'percentage' | 'date';
}

interface AccountTypeGroup {
  label: string;
  rows: Array<Record<string, unknown>>;
  total: number;
}

@Component({
  selector: 'app-dashboard-list-widget',
  standalone: false,
  templateUrl: './dashboard-list-widget.html',
  styleUrl: './dashboard-list-widget.scss',
})
export class DashboardListWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input({ required: true }) title = '';
  @Input({ required: true }) query!: ReportQuery;
  @Input({ required: true }) columns: DashboardListColumn[] = [];
  @Input() subtitle = '';
  @Input() emptyText = 'No records for this period.';
  @Input() maxRows = 12;
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';
  @Input() showAccountTypeDescription = false;
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 4 } };

  loading = false;
  error = '';
  result?: ReportResult;
  private request?: Subscription;

  constructor(
    private readonly reportQuery: ReportQueryService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('attr.style')
  get layoutStyles(): string {
    return dashboardLayoutStyle(this.layout);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query'] || changes['columns'] || changes['maxRows']) this.load();
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  get rows(): Array<Record<string, unknown>> {
    return this.result?.rows ?? [];
  }

  get accountTypeGroups(): AccountTypeGroup[] {
    const labels = new Map<string, string>([
      ['asset', 'Assets'], ['liability', 'Liabilities'], ['equity', 'Equity'],
      ['income', 'Income'], ['expense', 'Expenses'], ['off_balance', 'Off-balance accounts'],
      ['other', 'Other accounts'],
    ]);
    const grouped = new Map<string, AccountTypeGroup>();
    for (const row of this.rows) {
      const accountType = String(row['account_type'] ?? '').toLowerCase();
      const key = accountType.startsWith('asset') ? 'asset'
        : accountType.startsWith('liability') ? 'liability'
        : accountType.startsWith('equity') ? 'equity'
        : accountType.startsWith('income') ? 'income'
        : accountType.startsWith('expense') ? 'expense'
        : accountType.startsWith('off_balance') ? 'off_balance' : 'other';
      const group = grouped.get(key) ?? { label: labels.get(key) ?? 'Other accounts', rows: [], total: 0 };
      group.rows.push(row);
      group.total += Number(row['balance'] ?? 0);
      grouped.set(key, group);
    }
    return [...grouped.values()].sort((a, b) => [...labels.values()].indexOf(a.label) - [...labels.values()].indexOf(b.label));
  }

  get accountTypeNet(): number {
    return this.rows.reduce((total, row) => total + Number(row['balance'] ?? 0), 0);
  }

  formatValue(value: unknown, column: DashboardListColumn): string {
    if (value === null || value === undefined) return '—';
    if (column.format === 'number') {
      return new Intl.NumberFormat(this.locale, { maximumFractionDigits: 2 }).format(Number(value));
    }
    if (column.format === 'currency') {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currencyCode,
        maximumFractionDigits: 2,
      }).format(Number(value));
    }
    if (column.format === 'percentage') {
      return `${new Intl.NumberFormat(this.locale, { maximumFractionDigits: 1 }).format(Number(value))}%`;
    }
    if (column.format === 'date') {
      const parsed = new Date(String(value));
      return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString(this.locale);
    }
    return String(value);
  }

  accountTypeDescription(value: unknown): string {
    const accountType = String(value ?? '').trim().toLowerCase().replaceAll(' ', '_');
    const descriptions: Record<string, string> = {
      asset_receivable: 'Money customers still owe the company.',
      asset_cash: 'Money held in bank accounts or as cash.',
      asset_current: 'Stock and other assets expected to turn into cash soon.',
      asset_non_current: 'Long-term resources owned by the company.',
      asset_prepayments: 'Costs paid now for goods or services received later.',
      asset_fixed: 'Equipment, property and other long-lived assets.',
      liability_payable: 'Money the company still owes suppliers.',
      liability_credit_card: 'Amounts owed on company credit cards.',
      liability_current: 'Other amounts due within the near term.',
      liability_non_current: 'Amounts owed over a longer period.',
      equity: 'Owners’ stake in the company after its debts.',
      equity_unaffected: 'Current-year earnings carried in equity.',
      income: 'Money earned from the company’s normal sales.',
      income_other: 'Money earned from other sources.',
      expense: 'Costs of running the company.',
      expense_depreciation: 'The cost of long-lived assets spread over time.',
      expense_direct_cost: 'Costs directly tied to goods or services sold.',
      off_balance: 'Tracked commitments outside the balance sheet.',
      asset: 'Resources owned by the company.',
      liability: 'Amounts the company owes.',
    };
    return descriptions[accountType] ?? 'See the account details for this type.';
  }

  private load(): void {
    this.request?.unsubscribe();
    this.result = undefined;
    this.error = '';
    if (!this.query?.from || !this.query?.duration) {
      this.error = 'This dashboard list query must include both from and duration.';
      return;
    }
    if (this.columns.length === 0) {
      this.error = 'This dashboard list requires at least one column.';
      return;
    }

    const boundedQuery: ReportQuery = {
      ...this.query,
      limit: Math.max(1, Math.min(20, this.query.limit ?? this.maxRows, this.maxRows)),
      offset: 0,
      include_total: false,
    };
    this.loading = true;
    this.request = this.reportQuery.verify(boundedQuery).pipe(
      switchMap(() => this.reportQuery.execute(boundedQuery)),
    ).subscribe({
      next: (result) => {
        this.result = result;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.error = dashboardErrorMessage(error);
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

}
